import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ROUTES from "../../routes";
import "./privacypolicy.scss";
import useScreenSize from "../../hooks/useScreenSize";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [showScrollTop, setShowScrollTop] = useState(false);

  const handleLogin = () => {
    navigate(ROUTES.PATHS.LOGIN);
  };

  const handleScrollTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (<div className="privacy-page">
    <header>
      <div className="container">
        <nav>
          <a className="logo" href="/">
            <span className="logo-dot"></span>
            <strong>SerialMan</strong>
            {!isMobile && <span style={{ opacity: 0.6, fontSize: "0.9rem" }}>Queue & Appointments</span>}
          </a>
          <div className="links">
            {!isMobile && <a className="link" href="/#features">Features</a>}
            {!isMobile && <a className="link" href="/#pricing">Pricing</a>}
            <a className="link" href="/privacy-policy">Privacy</a>
            <a className="link" href="/terms">Terms</a>
            <a className="link" onClick={handleLogin}>Login</a>
          </div>
        </nav>
      </div>
    </header>

    <section className="hero-section">
      <div className="container">
        <h1>Privacy Policy</h1>
        <p className="lead">Last updated: April 2026</p>
        <p className="description">At HrnSoft, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our SerialMan application and services.</p>
      </div>
    </section>

    <div className="container policy-container">
      <main className="policy-content">
        <section id="introduction" className="policy-section">
          <h2>Introduction</h2>
          <p>
            This Privacy Policy explains how HrnSoft (SerialMan) collects and uses your information when you use our website and app.
          </p>
        </section>

        <section id="what-we-collect" className="policy-section">
          <h2>What We Collect</h2>
          <h3>When registering as a Service Center</h3>
          <ul>
            <li>Name, Address Line 1 &amp; 2</li>
            <li>Contact Person, Email Address, Mobile Number</li>
            <li>Business Type</li>
            <li>Location details: Division, District, Thana, and Area</li>
            <li>GPS coordinates (latitude &amp; longitude), collected only if you grant device location permission</li>
            <li>Login Name and password (stored securely)</li>
          </ul>
          <h3>When registering as a Service Taker</h3>
          <ul>
            <li>Name, Email Address, Mobile Number</li>
            <li>Gender</li>
            <li>Login Name and password (stored securely)</li>
          </ul>
          <h3>Automatically collected</h3>
          <ul>
            <li>Technical data such as device, browser, IP address, and app usage logs.</li>
            <li>Firebase device push token — collected only if you allow push notifications, used solely to deliver notifications to your device.</li>
          </ul>
        </section>

        <section id="how-we-use" className="policy-section">
          <h2>How We Use Data</h2>
          <ul>
            <li>To run and improve SerialMan features.</li>
            <li>To send important service updates and notifications.</li>
            <li>To provide support and keep the platform secure.</li>
          </ul>
        </section>

        <section id="sharing-security" className="policy-section">
          <h2>Sharing & Security</h2>
          <p>
            We do not sell your personal data. We only share data with trusted providers when needed to operate services like hosting, payments, messaging, and authentication. We use Firebase Cloud Messaging (FCM) by Google to deliver push notifications; your device token is transmitted to Google's servers for this purpose only, and only if you have granted notification permission.
          </p>
          <p>
            We use reasonable security measures to protect your information, but no online system can guarantee 100% security.
          </p>
        </section>

        <section id="your-rights" className="policy-section">
          <h2>Your Rights</h2>
          <p>
            You can request access, correction, or deletion of your personal data by contacting us.
          </p>
        </section>

        <section id="contact" className="policy-section">
          <h2>Contact</h2>
          <div className="contact-info">
            <p><strong>Email:</strong> privacy@hrnsoft.com</p>
            <p><strong>Address:</strong> HrnSoft Inc., Dhaka, Bangladesh</p>
          </div>

          <p style={{ marginTop: "24px", paddingTop: "24px", borderTop: "1px solid rgba(39, 105, 135, 0.1)" }}>
            <strong>Last Updated:</strong> April 15, 2026
          </p>
        </section>
      </main>
    </div>

    <footer>
      <div className="container" style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "30px 0" }}>
        <div className="copyright">© 2026 HrnSoft. All rights reserved.</div>
      </div>
    </footer>

    {showScrollTop && (
      <button 
        className="scroll-to-top"
        onClick={handleScrollTop}
        title="Scroll to top"
        aria-label="Scroll to top"
      >
        ↑
      </button>
    )}
  </div>
  );
}
